import React, { useState, useEffect } from 'react';
import { apiClient } from '../../apiClient';

const MaintenanceSettings = () => {
    const [maintenanceConfig, setMaintenanceConfig] = useState({
        enabled: false,
        startAt: '',
        endAt: '',
        message: '',
        maintenanceVersion: 0
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Formats a Date object into YYYY-MM-DDTHH:mm in JST
    const toJSTString = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        try {
            const formatter = new Intl.DateTimeFormat('ja-JP', {
                timeZone: 'Asia/Tokyo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            const parts = formatter.formatToParts(date);
            const getPart = (type) => parts.find(p => p.type === type).value;
            return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
        } catch (e) {
            console.error("Format error:", e);
            return '';
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await apiClient.get('/v1/admin/maintenance/config');
                if (response.ok) {
                    const data = await response.json();
                    setMaintenanceConfig({
                        ...data,
                        startAt: data.startAt ? toJSTString(new Date(data.startAt._seconds * 1000)) : '',
                        endAt: data.endAt ? toJSTString(new Date(data.endAt._seconds * 1000)) : ''
                    });
                }
            } catch (error) {
                console.error("Error fetching maintenance settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSaveMaintenance = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const convertToUTC = (jstString) => {
                if (!jstString) return null;
                // Treat input string as JST (+09:00) by appending the offset
                // This forces the constructor to interpret the time as JST regardless of browser locale
                const isoWithJST = jstString.includes('T') ? `${jstString}:00+09:00` : `${jstString}T00:00:00+09:00`;
                return new Date(isoWithJST).toISOString();
            };

            const payload = {
                ...maintenanceConfig,
                startAt: convertToUTC(maintenanceConfig.startAt),
                endAt: convertToUTC(maintenanceConfig.endAt),
            };

            const response = await apiClient.post('/v1/admin/maintenance/config', payload);
            if (response.ok) {
                setMessage('メンテナンス設定を保存しました。');
            } else {
                const errorData = await response.json();
                setMessage(`保存に失敗しました: ${errorData.error || '不明なエラー'}`);
            }
        } catch (error) {
            console.error("Error saving maintenance settings:", error);
            setMessage('エラーが発生しました。');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8">読み込み中...</div>;

    return (
        <div className="container mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold mb-6">メンテナンスモード設定</h2>

            {message && (
                <div className={`p-4 mb-4 rounded ${message.includes('失敗') || message.includes('エラー') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {message}
                </div>
            )}

            <div className="bg-white shadow rounded-lg p-6 max-w-2xl">
                <form onSubmit={handleSaveMaintenance} className="space-y-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="maintenanceEnabled"
                            checked={maintenanceConfig.enabled}
                            onChange={(e) => setMaintenanceConfig({ ...maintenanceConfig, enabled: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="maintenanceEnabled" className="ml-2 block text-sm text-gray-900 font-bold">
                            メンテナンスモードを有効にする
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">開始日時 (JST)</label>
                        <input
                            type="datetime-local"
                            value={maintenanceConfig.startAt}
                            onChange={(e) => setMaintenanceConfig({ ...maintenanceConfig, startAt: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                            required={maintenanceConfig.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">終了日時 (JST)</label>
                        <input
                            type="datetime-local"
                            value={maintenanceConfig.endAt}
                            onChange={(e) => setMaintenanceConfig({ ...maintenanceConfig, endAt: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                            required={maintenanceConfig.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">メンテナンスメッセージ</label>
                        <textarea
                            rows="4"
                            value={maintenanceConfig.message}
                            onChange={(e) => setMaintenanceConfig({ ...maintenanceConfig, message: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="システムメンテナンス中です。"
                        ></textarea>
                        <p className="mt-1 text-xs text-gray-500">最大1000文字</p>
                    </div>

                    <div className="text-sm text-gray-500">
                        現在のバージョン: {maintenanceConfig.maintenanceVersion}
                    </div>

                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400 font-bold"
                    >
                        {saving ? '保存中...' : 'メンテナンス設定を保存'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MaintenanceSettings;
